<template>
  <div class="flex flex-col items-center justify-center h-screen w-full">
    <div class="bg-white rounded-2xl shadow-xl p-8 w-full max-w-3xl">
      <div class="flex flex-col items-center mb-6">
        <h2 class="text-2xl font-bold text-gray-800 mb-2">Зарегистрируйте свой цветочный магазин</h2>
        <p class="text-gray-500 text-sm">Создайте свою учетную запись и начните управлять продажами</p>
      </div>
      <Toast/>
      <Form v-slot="$form" :resolver="resolver" :initialValues="initialValues" @submit="registerShop"  class="space-y-4">
        <div class="flex flex-col gap-4">
            <div class="grid grid-cols-12 gap-4">
                <div class="col-span-6">
                    <FloatLabel variant="on">
                        <InputText id="shopName" name="shopName" placeholder="Наименование магазина" required autofocus fluid/>
                        <Message v-if="$form.shopName?.invalid" severity="error" size="small" variant="simple">{{ $form.shopName.error?.message }}</Message> 
                    </FloatLabel>
                </div>
                <div class="col-span-6">
                    <FloatLabel variant="on" class="col-span-6">
                        <InputText id="ownerName" name="$ownerName" placeholder="Владелец" required autofocus fluid/>
                        <Message v-if="$form.ownerName?.invalid" severity="error" size="small" variant="simple">{{ $form.ownerName.error?.message }}</Message> 
                    </FloatLabel>
                </div>
            </div>

            <div class="grid grid-cols-12 gap-4">
                <div class="col-span-6">
                    <FloatLabel variant="on">
                        <InputText id="email" name="email" required placeholder="Email" fluid/>
                        <Message v-if="$form.email?.invalid" severity="error" size="small" variant="simple">{{ $form.email.error?.message }}</Message>  
                    </FloatLabel>
                </div>
                <div class="col-span-6">
                    <FloatLabel variant="on" class="col-span-6">
                        <InputMask id="phone" name="$form.phone" required mask="8(799)999-99-99" placeholder="8(799)999-99-99" fluid/>
                        <Message v-if="$form.phone?.invalid" severity="error" size="small" variant="simple">{{ $form.phone.error?.message }}</Message>  
                    </FloatLabel>
                </div>
            </div>
            <div class="grid grid-cols-12 gap-4">
                <div class="col-span-6">
                    <FloatLabel variant="on">
                        <InputText id="city" name="city" required placeholder="Город" fluid/>
                        <Message v-if="$form.city?.invalid" severity="error" size="small" variant="simple">{{ $form.city.error?.message }}</Message> 
                    </FloatLabel>
                </div>
                <div class="col-span-6">
                    <FloatLabel variant="on" class="col-span-6">
                        <InputText id="address" name="address" placeholder="Адрес" required fluid/>
                        <Message v-if="$form.address?.invalid" severity="error" size="small" variant="simple">{{ $form.address.error?.message }}</Message> 
                    </FloatLabel>
                </div>
            </div>
            <div class="grid grid-cols-12 gap-4">
                <div class="col-span-6">
                    <FloatLabel variant="on">
                        <label for="password" class="z-10 block"> Пароль </label>
                        <Password id="password" name="password" toggleMask autofocus autocomplete="new-password" fluid required
                            weakLabel="Слабый пароль" mediumLabel="Средний пароль" strongLabel="Сильный пароль" promptLabel="Введите пароль"
                        >                              
                            <template #footer>
                                <Divider />
                                <ul class="pl-2 my-0 leading-normal text-sm">
                                    <li> По крайней мере одна строчная буква</li>
                                    <li> По крайней мере одна заглавная буква</li>
                                    <li> По крайней мере одно числовое значение</li>
                                    <li> Минимум 8 символов</li>
                                </ul>
                            </template>
                        </Password>
                    </FloatLabel>
                </div>
                <div class="col-span-6">
                    <FloatLabel variant="on">
                        <label for="confirmPassword" class="z-10 block"> Подтверждение пароля </label>
                        <Password id="confirmPassword" name="confirmPassword" toggleMask fluid required autocomplete="new-password" :invalid="$form.password !== $form.confirmPassword" :feedback="false" ></Password>
                    </FloatLabel>
                </div>
            </div>
        </div>

        <div class="grid grid-cols-12 gap-4">
             <div class="col-span-6">
                <Button severity="danger" @click="goToMainPage()" class="w-full"> Назад</Button>
            </div>
            <div class="col-span-6">
                <Button severity="success" type="submit" class="w-full">
                    Зарегистрироваться
                </Button>
            </div>
        </div>
      </Form>

        <div class="flex items-center gap-2 mt-2">
            <Checkbox v-model="terms" size="small"  :binary="true"/>
            <label for="terms" class="text-sm">Я согласен с <span class="text-blue-400 hover:underline cursor-pointer">Условиями использования и Политикой конфиденциальности</span></label>
            <p>Checked: {{ terms }}</p>
        </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { Form } from '@primevue/forms';
import { useToast } from 'primevue/usetoast'
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { z } from 'zod';
import { useRouter } from 'vue-router'
import Toast from 'primevue/toast'

const toast = useToast()
const router = useRouter()
const submitted = ref(false)
const loading = ref(false)
let terms = ref(false)

const initialValues = ref({
  shopName: '',
  ownerName: '',
  email: '',
  phone: '',
  city: '',
  address: '',
  password: '',
  confirmPassword: ''
})

const resolver = ref(zodResolver(
    z.object({
        shopName: z.string().min(1, { message: 'Требуется наименование магазина.' }),
        ownerName: z.string().min(1, { message: 'Требуется ФИО владелец.' }),
        phone: z.string().min(1, { message: 'Требуется рабочий номер телефона.' }),
        email: z.string().min(1, { message: 'Требуется адрес электронной почты.' }).email({ message: 'Неверный адрес электронной почты.' }),
        city: z.string().min(1, { message: 'Требуется город.' }),
        address: z.string().min(1, { message: 'Требуется адрес.' }),
        password: z.string().min(1, { message: 'Требуется пароль.' }),
        confirmPassword: z.string().min(1, { message: 'Требуется подтверждение пароля.' }),
        // terms: z.object({ acceptTerms: z.literal(true, { errorMap: () => ({ message: "Подтвердите условия использования и политику конфиденциальности" })})})
    })
));

const registerShop = () => {
    console.log(terms)
    if (!initialValues.value.terms) {
        toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Необходимо согласиться с условиями использования и политикой конфиденциальности', life: 3000 })
      return
    }   else {
        axios.post('http://localhost:3000/api/v1/shops', initialValues.value).then(response => {
            
        }).catch(error => {
            
        })
        toast.add({ severity: 'success', summary: 'Успешно', detail: 'Регистрация прошла успешно', life: 3000 })
        router.push({ name: 'Account'})
    }
}
const goToMainPage = () => {
    router.push({ name: 'MainPage' })
}
</script>

<style>
</style>
